import { useMemo, useState } from 'preact/hooks';
import { BackupError, generateMnemonic, isWord, restoreBackup, suggestions, WORDLIST, normalizeWords } from '../core';
import { formatDate } from '../app/format';
import { LEGAL_URL, LEGAL_VERSION, type Language, Prefs } from '../app/prefs';
import legalFR from '../legal/conditions-fr.md';
import legalEN from '../legal/conditions-en.md';
import { BrandLockup, BrandMark, Group, PinPad, PlainButton, PrimaryButton, SecondaryButton, Sheet, copyText, haptic } from './components';
import { Check, CheckCircle, Doc, Globe, KEY_POINT_ICONS, Seal, Share } from './icons';
import { ui, useApp } from './state';

// ---- Language (first launch) ----

export function LanguageSetup() {
  const [sel, setSel] = useState<Language>(Prefs.deviceLanguage());
  const { s } = useApp();
  const t = sel === 'fr' ? { title: 'Choisis ta langue', sub: 'Tu pourras la changer en tout temps dans Paramètres.', go: 'Continuer' }
    : { title: 'Choose your language', sub: 'You can change it at any time in Settings.', go: 'Continue' };
  void s;
  return (
    <div class="screen center-screen">
      <div class="spacer" />
      <BrandLockup markSize={84} />
      <div class="spacer" />
      <div class="stack-6 text-center">
        <h1 class="title2">{t.title}</h1>
        <p class="dim">{t.sub}</p>
      </div>
      <div class="card list">
        {(['fr', 'en'] as Language[]).map((l) => (
          <button type="button" class="list-item" onClick={() => setSel(l)} aria-pressed={sel === l}>
            <span>{l === 'fr' ? 'Français' : 'English'}</span>
            {sel === l && <Check />}
          </button>
        ))}
      </div>
      <PrimaryButton onClick={() => ui.setLanguage(sel)}>{t.go}</PrimaryButton>
    </div>
  );
}

// ---- Terms ----

export function LegalAcceptance({ isUpdate }: { isUpdate: boolean }) {
  const { s } = useApp();
  const [age, setAge] = useState(false);
  const [risk, setRisk] = useState(false);
  const [terms, setTerms] = useState(false);
  const all = age && risk && terms;
  return (
    <div class="screen">
      <div class="row-between">
        <BrandMark size={40} />
        <LanguageMenu />
      </div>
      <h1 class="large-title">{isUpdate ? s.legalUpdatedTitle : s.legalTitle}</h1>
      <p class="dim">{isUpdate ? s.legalUpdatedSubtitle : s.legalSubtitle}</p>
      {isUpdate && (
        <div class="card pad">
          <div class="section-title">{s.legalWhatChanged}</div>
          <p class="strong">{s.legalUpdateSummary}</p>
        </div>
      )}
      <div class="card pad stack-18">
        {s.legalKeyPoints.map((p) => {
          const Icon = KEY_POINT_ICONS[p.icon];
          return (
            <div class="keypoint">
              <span class="icon-circle"><Icon size={18} strokeWidth={2.4} /></span>
              <div>
                <div class="headline">{p.title}</div>
                <div class="dim small">{p.detail}</div>
              </div>
            </div>
          );
        })}
      </div>
      <button type="button" class="glass-pill wide" onClick={() => ui.open({ type: 'legal' })}>
        <Doc size={20} /> {s.legalReadFull}
      </button>
      <div class="stack-14">
        <CheckRow on={age} set={setAge} text={s.legalCheckAge} />
        <CheckRow on={risk} set={setRisk} text={s.legalCheckRisk} />
        <CheckRow on={terms} set={setTerms} text={s.legalCheckTerms} />
      </div>
      <div class="bottom-bar">
        <PrimaryButton disabled={!all} onClick={() => { Prefs.acceptLegal(); ui.setTab('wallet'); ui.closeAll(); }}>
          {s.legalAcceptButton}
        </PrimaryButton>
      </div>
    </div>
  );
}

function CheckRow({ on, set, text }: { on: boolean; set: (v: boolean) => void; text: string }) {
  return (
    <button type="button" role="checkbox" aria-checked={on} class={`check-row ${on ? 'on' : ''}`} onClick={() => set(!on)}>
      <span class="box">{on && <Check size={16} strokeWidth={3} />}</span>
      <span>{text}</span>
    </button>
  );
}

export function LanguageMenu() {
  const { s } = useApp();
  const cur = Prefs.currentLanguage();
  void s;
  return (
    <button type="button" class="glass-pill" onClick={() => ui.setLanguage(cur === 'fr' ? 'en' : 'fr')}>
      <Globe size={18} /> {cur === 'fr' ? 'English' : 'Français'}
    </button>
  );
}

/** Renders docs/legal/conditions-*.md (headings, paragraphs, bullets, highlighted "> " blocks, **bold**). */
export function LegalDocument() {
  const { s } = useApp();
  const text = Prefs.currentLanguage() === 'fr' ? legalFR : legalEN;
  const blocks = useMemo(() => text.split('\n').map((l) => l.trim()).filter(Boolean), [text]);
  const at = Prefs.acceptedLegalAt();
  return (
    <Sheet title={s.legalDocumentTitle}
      left={<PlainButton aria-label={s.share} onClick={() => shareOrCopy(LEGAL_URL)}><Share /></PlainButton>}
      right={<PlainButton onClick={() => ui.close()}>{s.done}</PlainButton>}>
      <article class="legal">
        {blocks.map((l) => {
          if (l.startsWith('### ')) return <h3>{inline(l.slice(4))}</h3>;
          if (l.startsWith('## ')) return <h2>{inline(l.slice(3))}</h2>;
          if (l.startsWith('# ')) return <h1>{inline(l.slice(2))}</h1>;
          if (l.startsWith('- ')) return <p class="bullet">{inline(l.slice(2))}</p>;
          if (l.startsWith('> ')) return <p class="callout">{inline(l.slice(2))}</p>;
          return <p>{inline(l)}</p>;
        })}
        {at && Prefs.acceptedLegalVersion() === LEGAL_VERSION && <p class="dim small">{s.legalAcceptedOn(formatDate(at))}</p>}
      </article>
    </Sheet>
  );
}

/** **bold** and *italic* only; everything else is plain text (no HTML injection). */
function inline(t: string) {
  const out: (string | preact.JSX.Element)[] = [];
  const re = /\*\*(.+?)\*\*|\*(.+?)\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(t))) {
    if (m.index > last) out.push(t.slice(last, m.index));
    out.push(m[1] !== undefined ? <strong>{m[1]}</strong> : <em>{m[2]}</em>);
    last = m.index + m[0].length;
  }
  if (last < t.length) out.push(t.slice(last));
  return out;
}

export async function shareOrCopy(text: string) {
  try {
    if (navigator.share) await navigator.share({ url: text.startsWith('http') ? text : undefined, text: text.startsWith('http') ? undefined : text });
    else await copyText(text);
  } catch {}
}

// ---- Welcome / create / restore ----

export function Welcome() {
  const { s, model } = useApp();
  const [restoring, setRestoring] = useState(false);
  if (restoring) return <Restore onBack={() => setRestoring(false)} />;
  return (
    <div class="screen center-screen">
      <div class="spacer" />
      <BrandLockup markSize={112} />
      <div class="spacer" />
      <p class="tagline">{s.welcomeTagline}</p>
      <PrimaryButton onClick={() => model.beginCreate(generateMnemonic())}>{s.createWallet}</PrimaryButton>
      <SecondaryButton onClick={() => setRestoring(true)}>{s.haveWallet}</SecondaryButton>
    </div>
  );
}

function Restore({ onBack }: { onBack: () => void }) {
  const { s, model } = useApp();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const words = normalizeWords(text);
  const prefix = /\s$/.test(text) ? '' : words[words.length - 1] ?? '';
  const sugg = prefix && !isWord(prefix) ? suggestions(prefix) : [];
  const countOK = [12, 15, 18, 21, 24].includes(words.length);
  const complete = (w: string) => {
    const ws = [...words];
    ws.pop();
    ws.push(w);
    setText(ws.join(' ') + ' ');
  };
  const restore = () => {
    try {
      model.beginRestore(text);
    } catch (e) {
      setError(model.describe(e));
    }
  };
  return (
    <Sheet title={s.restoreTitle} left={<PlainButton onClick={onBack}>‹ {s.cancel}</PlainButton>}>
      <Group title={s.restoreHeader} footer={s.restoreWordCount(words.length)}>
        <textarea class="phrase-input" rows={4} autocapitalize="off" autocomplete="off" spellcheck={false}
          autoFocus value={text} onInput={(e) => { setText((e.target as HTMLTextAreaElement).value); setError(null); }} />
      </Group>
      {sugg.length > 0 && (
        <Group title={s.restoreSuggestions}>
          <div class="chips">{sugg.map((w) => <button type="button" class="chip" onClick={() => complete(w)}>{w}</button>)}</div>
        </Group>
      )}
      {error && <p class="error">{error}</p>}
      <PrimaryButton disabled={!countOK} onClick={restore}>{s.restoreButton}</PrimaryButton>
      <p class="dim small text-center">{s.restoreFooter}</p>
      <RestoreFromFile />
    </Sheet>
  );
}

/** Restore from an encrypted backup file (same file on iPhone, Android and web). */
function RestoreFromFile() {
  const { s, model } = useApp();
  const [content, setContent] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const message = (e: unknown) => {
    if (e instanceof BackupError) {
      switch (e.kind) {
        case 'notABackup': return s.fileBackupErrNot;
        case 'unsupported': return s.fileBackupErrUnsupported;
        case 'wrongNetwork': return s.fileBackupErrNetwork;
        case 'wrongPasswordOrAltered': return s.fileBackupErrPassword;
        case 'invalidPhrase': return s.fileBackupErrPhrase;
      }
    }
    return s.genericError;
  };

  const restore = async () => {
    if (content === null) return;
    setBusy(true);
    setError(null);
    try {
      model.beginRestore(await restoreBackup(content, password));
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Group title={s.fileBackupRestoreRow}>
      <div class="pad stack-10">
        <label class="btn btn-secondary">
          {fileName || s.fileBackupChoose}
          <input type="file" accept=".coinwowbackup,application/json,.json" hidden onChange={async (e) => {
            const f = (e.target as HTMLInputElement).files?.[0];
            setError(null);
            if (!f) return;
            setFileName(f.name);
            setContent(f.size > 4096 ? '' : await f.text());
          }} />
        </label>
        {content !== null && (
          <input class="row-input" type="password" placeholder={s.fileBackupPassword} autocomplete="off" autocapitalize="off" spellcheck={false}
            value={password} onInput={(e) => { setPassword((e.target as HTMLInputElement).value); setError(null); }} />
        )}
        {error && <p class="error small">{error}</p>}
        <PrimaryButton disabled={content === null || password.length === 0 || busy} onClick={() => void restore()}>{s.fileBackupRestore}</PrimaryButton>
      </div>
    </Group>
  );
}

// ---- Passcode creation (after create or restore) ----

export function PinSetup({ restored }: { restored: boolean }) {
  const { s, model } = useApp();
  const [first, setFirst] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const weak = (p: string) => /^(\d)\1{5}$/.test(p) || '0123456789'.includes(p) || '9876543210'.includes(p);
  return (
    <div class="screen pin-screen">
      <PinPad
        title={first === null ? s.pinCreateTitle : s.pinConfirmTitle}
        subtitle={first === null ? s.pinCreateSubtitle : undefined}
        error={error}
        busy={busy}
        onComplete={async (pin) => {
          if (first === null) {
            if (weak(pin)) return setError(s.pinWeak);
            setError(null);
            setFirst(pin);
            return;
          }
          if (pin !== first) {
            setFirst(null);
            return setError(s.pinMismatch);
          }
          setBusy(true);
          try {
            await model.finishSetup(pin, restored);
          } catch (e) {
            setBusy(false);
            setError(model.describe(e));
          }
        }}
        footer={<PlainButton class="dim" onClick={() => model.cancelCreate()}>{s.cancel}</PlainButton>}
      />
    </div>
  );
}

// ---- Backup of the recovery phrase ----

export function BackupFlow({ phrase, mandatory, onFinish, onCancel }: {
  phrase: string; mandatory: boolean; onFinish: () => void; onCancel?: () => void;
}) {
  const { s } = useApp();
  const words = phrase.split(' ');
  const [step, setStep] = useState<'intro' | 'words' | 'verify' | 'done'>('intro');
  const [quiz, setQuiz] = useState<number[]>([]);
  const [pos, setPos] = useState(0);
  const [wrong, setWrong] = useState(false);

  const options = useMemo(() => {
    if (step !== 'verify' || pos >= quiz.length) return [];
    const set = new Set([words[quiz[pos]]]);
    const rnd = new Uint32Array(8);
    while (set.size < 4) {
      crypto.getRandomValues(rnd);
      set.add(WORDLIST[rnd[0] % WORDLIST.length]);
    }
    return [...set].sort(() => (crypto.getRandomValues(new Uint8Array(1))[0] & 1 ? 1 : -1));
  }, [step, pos, quiz]);

  const startQuiz = () => {
    const idx = [...words.keys()];
    const picked: number[] = [];
    while (picked.length < 3) {
      const r = crypto.getRandomValues(new Uint32Array(1))[0] % idx.length;
      picked.push(idx.splice(r, 1)[0]);
    }
    setQuiz(picked.sort((a, b) => a - b));
    setPos(0);
    setWrong(false);
    setStep('verify');
  };

  const answer = (w: string) => {
    if (w === words[quiz[pos]]) {
      setWrong(false);
      if (pos + 1 === quiz.length) {
        haptic();
        setStep('done');
      } else setPos(pos + 1);
    } else {
      haptic();
      setWrong(true);
    }
  };

  const later = !mandatory && step !== 'done' ? <PlainButton onClick={onFinish}>{s.later}</PlainButton> : undefined;
  const cancel = mandatory && onCancel && step === 'intro' ? <PlainButton onClick={onCancel}>‹ {s.cancel}</PlainButton> : undefined;

  return (
    <Sheet left={cancel ?? later} class="backup">
      {step === 'intro' && (
        <div class="stack-22 grow">
          <div class="spacer" />
          <span class="icon-circle big"><KEY_POINT_ICONS.key size={30} strokeWidth={2.4} /></span>
          <h1 class="large-title">{s.backupIntroTitle}</h1>
          <div class="stack-16">
            {s.backupIntroPoints.map((p) => (
              <div class="keypoint"><CheckCircle size={22} /><span>{p}</span></div>
            ))}
          </div>
          {mandatory && <p class="dim small">{s.backupMandatoryNote}</p>}
          <div class="spacer" />
          <PrimaryButton onClick={() => setStep('words')}>{s.backupShowWords}</PrimaryButton>
        </div>
      )}
      {step === 'words' && (
        <div class="stack-20 grow">
          <h1 class="title2">{s.backupWordsTitle}</h1>
          <p class="dim">{s.backupWordsSubtitle}</p>
          <div class="word-grid secret">
            {words.map((w, i) => (
              <div class="word"><span class="num">{i + 1}</span><span class="mono strong">{w}</span></div>
            ))}
          </div>
          <p class="dim small">{s.backupNoScreenshot}</p>
          <div class="spacer" />
          <PrimaryButton onClick={startQuiz}>{s.backupWroteThem}</PrimaryButton>
        </div>
      )}
      {step === 'verify' && (
        <div class="stack-20 grow">
          <h1 class="title2">{s.backupVerifyTitle}</h1>
          <p class="dim">{s.backupVerifySubtitle(pos + 1, quiz.length)}</p>
          <div class="quiz-word">{s.backupWordNumber(quiz[pos] + 1)}</div>
          <div class="option-grid">
            {options.map((o) => <button type="button" class="glass-tile option mono" onClick={() => answer(o)}>{o}</button>)}
          </div>
          {wrong && <p class="error text-center">{s.backupWrongWord}</p>}
          <div class="spacer" />
          <SecondaryButton onClick={() => setStep('words')}>{s.backupSeeWordsAgain}</SecondaryButton>
        </div>
      )}
      {step === 'done' && (
        <div class="stack-20 grow text-center">
          <div class="spacer" />
          <Seal size={72} strokeWidth={1.5} />
          <h1 class="large-title">{s.backupDoneTitle}</h1>
          <p class="dim">{s.backupDoneText}</p>
          <div class="spacer" />
          <PrimaryButton onClick={onFinish}>{s.done}</PrimaryButton>
        </div>
      )}
    </Sheet>
  );
}
